import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import type { SportOrShared } from '#src/__generated__/globalTypes.ts';
import { NotificationPreferenceChannel } from '#src/__generated__/globalTypes.ts';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { NotificationPreference } from '#src/components/settings/NotificationPreferences/NotificationPreference/index.tsx';
import { Section } from '#src/components/settings/NotificationPreferences/Section/index.tsx';

import type {
  NotificationSection_notificationSection,
  UpdateSectionNotificationPreferencesMutation,
  UpdateSectionNotificationPreferencesMutationVariables,
} from './__generated__/index.graphql.ts';

const UPDATE_SECTION_NOTIFICATION_PREFERENCES_MUTATION = gql`
  mutation UpdateSectionNotificationPreferencesMutation(
    $input: updateSectionNotificationPreferencesInput!
    $sportOrShared: SportOrShared!
  ) {
    updateSectionNotificationPreferences(input: $input) {
      section {
        slug
        name
        myNotificationPreferences(sport: $sportOrShared) {
          name
          channels
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateSectionNotificationPreferencesMutation,
  UpdateSectionNotificationPreferencesMutationVariables
>;

type Props = {
  notificationSection: NotificationSection_notificationSection;
  sport: SportOrShared;
  onChange: (
    name: string,
    newValue: NotificationPreferenceChannel[] | Json
  ) => void;
};

export const NotificationSection = ({
  notificationSection,
  sport,
  onChange,
}: Props) => {
  const [mutate] = useMutation(
    UPDATE_SECTION_NOTIFICATION_PREFERENCES_MUTATION
  );

  if (notificationSection.myNotificationPreferences.length === 0) {
    return null;
  }

  const hasAtLeastOnePreferenceEnabled =
    notificationSection.myNotificationPreferences.some(preference => {
      const enabledChannels = preference.channels ?? preference.defaultChannels;

      return enabledChannels.length > 0;
    });

  return (
    <Section
      title={notificationSection.name}
      actions={
        <ButtonBase
          onClick={() => {
            mutate({
              variables: {
                sportOrShared: sport,
                input: {
                  section: notificationSection.slug,
                  sport,
                  channels: hasAtLeastOnePreferenceEnabled
                    ? []
                    : Object.values(NotificationPreferenceChannel),
                },
              },
            });
          }}
        >
          <LabelM bold color="var(--c-nd-600)">
            {hasAtLeastOnePreferenceEnabled ? (
              <FormattedMessage defaultMessage="Disable all" />
            ) : (
              <FormattedMessage defaultMessage="Enable all" />
            )}
          </LabelM>
        </ButtonBase>
      }
    >
      {notificationSection.myNotificationPreferences.map(preference => (
        <NotificationPreference
          key={`${preference.name}-${sport}`}
          preference={preference}
          onChange={newValue => {
            onChange(preference.name, newValue);
          }}
        />
      ))}
    </Section>
  );
};

NotificationSection.fragments = {
  notificationSection: gql`
    fragment NotificationSection_notificationSection on NotificationSection {
      slug
      name
      myNotificationPreferences(sport: $sportOrShared) {
        name
        channels
        defaultChannels
        ...NotificationPreference_notificationPreference
      }
    }
    ${NotificationPreference.fragments.notificationPreference}
  ` as TypedDocumentNode<NotificationSection_notificationSection>,
};
